import { useParams, useNavigate, Link } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  useAdminTenantDetail,
  useEditTenant,
  useGrantCredits,
  useAdjustMonthlyCredits,
  useDisableTenant,
  useEnableTenant,
} from "@/hooks/api/useAdminApi";
import { ArrowLeft, Pencil, Plus, Settings, Ban, CheckCircle, Users } from "lucide-react";

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: tenant, isLoading, error } = useAdminTenantDetail(id!);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [grantCreditsOpen, setGrantCreditsOpen] = useState(false);
  const [adjustMonthlyOpen, setAdjustMonthlyOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);

  const [editData, setEditData] = useState({ companyName: "", slug: "" });
  const [creditAmount, setCreditAmount] = useState("");
  const [monthlyCredits, setMonthlyCredits] = useState("");

  const editMutation = useEditTenant();
  const grantCreditsMutation = useGrantCredits();
  const adjustMonthlyMutation = useAdjustMonthlyCredits();
  const disableMutation = useDisableTenant();
  const enableMutation = useEnableTenant();

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="p-6">
        <div className="text-destructive">Error loading tenant details</div>
      </div>
    );
  }

  const handleEdit = () => {
    setEditData({ companyName: tenant.companyName, slug: tenant.slug });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = () => {
    editMutation.mutate(
      { tenantId: id!, data: editData },
      {
        onSuccess: () => {
          toast.success("Tenant updated successfully");
          setEditDialogOpen(false);
        },
        onError: () => {
          toast.error("Failed to update tenant");
        },
      }
    );
  };

  const handleGrantCredits = () => {
    const amount = parseInt(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    grantCreditsMutation.mutate(
      { tenantId: id!, amount },
      {
        onSuccess: () => {
          toast.success(`Granted ${amount} credits`);
          setGrantCreditsOpen(false);
          setCreditAmount("");
        },
        onError: () => {
          toast.error("Failed to grant credits");
        },
      }
    );
  };

  const handleAdjustMonthly = () => {
    const amount = parseInt(monthlyCredits);
    if (isNaN(amount) || amount < 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    adjustMonthlyMutation.mutate(
      { tenantId: id!, monthlyCredits: amount },
      {
        onSuccess: () => {
          toast.success(`Monthly credits set to ${amount}`);
          setAdjustMonthlyOpen(false);
          setMonthlyCredits("");
        },
        onError: () => {
          toast.error("Failed to adjust monthly credits");
        },
      }
    );
  };

  const handleToggleStatus = () => {
    if (tenant.isDisabled) {
      enableMutation.mutate(id!, {
        onSuccess: () => {
          toast.success("Tenant enabled");
        },
        onError: () => {
          toast.error("Failed to enable tenant");
        },
      });
    } else {
      disableMutation.mutate(id!, {
        onSuccess: () => {
          toast.success("Tenant disabled");
          setDisableConfirmOpen(false);
        },
        onError: () => {
          toast.error("Failed to disable tenant");
        },
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/tenants")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{tenant.companyName}</h1>
            <p className="text-muted-foreground">{tenant.slug}</p>
          </div>
          <Badge variant={tenant.isDisabled ? "destructive" : "success"}>
            {tenant.isDisabled ? "Disabled" : "Enabled"}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleEdit}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={() => setGrantCreditsOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Grant Credits
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAdjustMonthlyOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Adjust Monthly
          </Button>
          <Button
            variant={tenant.isDisabled ? "default" : "destructive"}
            size="sm"
            onClick={() => {
              if (tenant.isDisabled) {
                handleToggleStatus();
              } else {
                setDisableConfirmOpen(true);
              }
            }}
          >
            {tenant.isDisabled ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Enable
              </>
            ) : (
              <>
                <Ban className="h-4 w-4 mr-2" />
                Disable
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/tenants/${id}/leads`)}>
            <Users className="h-4 w-4 mr-2" />
            View Leads
          </Button>
        </div>
      </div>

      {/* Company Info */}
      <Card>
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Company Name:</span>
            <span className="font-medium">{tenant.companyName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Slug:</span>
            <span className="font-medium">{tenant.slug}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Owner Email:</span>
            <span className="font-medium">{tenant.ownerEmail || "N/A"}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span className="font-medium">{new Date(tenant.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Updated:</span>
            <span className="font-medium">{new Date(tenant.updatedAt).toLocaleDateString()}</span>
          </div>
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Status:</span>
            <Badge>{tenant.subscriptionStatus}</Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Tier:</span>
            <span className="font-medium">{tenant.subscriptionTier}</span>
          </div>
          {tenant.trialEndsAt && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Trial Ends:</span>
              <span className="font-medium">{new Date(tenant.trialEndsAt).toLocaleDateString()}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credits */}
      <Card>
        <CardHeader>
          <CardTitle>Credits</CardTitle>
          <CardDescription>Generation credits breakdown</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Monthly:</span>
            <span className="font-medium">{tenant.creditBreakdown.monthly}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Addon:</span>
            <span className="font-medium">{tenant.creditBreakdown.addon}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Rollover:</span>
            <span className="font-medium">{tenant.creditBreakdown.rollover}</span>
          </div>
          <div className="flex justify-between font-semibold text-lg pt-2 border-t">
            <span>Total:</span>
            <span>{tenant.creditBreakdown.total}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Cap:</span>
            <span className="font-medium">{tenant.creditBreakdown.cap}</span>
          </div>
        </CardContent>
      </Card>

      {/* Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Leads:</span>
            <span className="font-medium">{tenant.leadCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Generations:</span>
            <span className="font-medium">{tenant.generationCount}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Generations This Month:</span>
            <span className="font-medium">{tenant.generationsThisMonth}</span>
          </div>
        </CardContent>
      </Card>

      {/* Integration */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Webhook URL:</span>
            <span className="font-medium truncate max-w-md">{tenant.webhookUrl || "Not set"}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Webhook Enabled:</span>
            <Badge variant={tenant.webhookEnabled ? "success" : "secondary"}>
              {tenant.webhookEnabled ? "Yes" : "No"}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Email Notifications:</span>
            <Badge variant={tenant.emailNotificationsEnabled ? "success" : "secondary"}>
              {tenant.emailNotificationsEnabled ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogDescription>Update company information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                value={editData.companyName}
                onChange={(e) => setEditData({ ...editData, companyName: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={editData.slug}
                onChange={(e) => setEditData({ ...editData, slug: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} disabled={editMutation.isPending}>
              {editMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Credits Dialog */}
      <Dialog open={grantCreditsOpen} onOpenChange={setGrantCreditsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Credits</DialogTitle>
            <DialogDescription>Add addon credits to this tenant</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="creditAmount">Amount</Label>
            <Input
              id="creditAmount"
              type="number"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantCreditsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGrantCredits} disabled={grantCreditsMutation.isPending}>
              {grantCreditsMutation.isPending ? "Granting..." : "Grant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Monthly Dialog */}
      <Dialog open={adjustMonthlyOpen} onOpenChange={setAdjustMonthlyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Monthly Credits</DialogTitle>
            <DialogDescription>Set the monthly credit allocation</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="monthlyCredits">Monthly Credits</Label>
            <Input
              id="monthlyCredits"
              type="number"
              value={monthlyCredits}
              onChange={(e) => setMonthlyCredits(e.target.value)}
              placeholder="Enter amount"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustMonthlyOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdjustMonthly} disabled={adjustMonthlyMutation.isPending}>
              {adjustMonthlyMutation.isPending ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Confirmation */}
      <AlertDialog open={disableConfirmOpen} onOpenChange={setDisableConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent the tenant from accessing the platform. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleStatus} className="bg-destructive">
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
